import { Polygon, MultiPolygon, Point } from "@turf/helpers";
import { Color } from "@deck.gl/core";
import { DataFilterExtension } from "@deck.gl/extensions";
import { GeoJsonLayerProps } from "@deck.gl/layers";
import { color } from "d3-color";
import { SITES_CLUSTERING_CONFIG, SitesFilters } from "@landtechnologies/search-layer-contracts";

import { showError } from "src/js/stores/user/actions/addNotification";
import { ENVIRONMENT } from "src/js/util/environment";

import {
  ClusteredViewportSearchMapLayer,
  ClusteredViewportSearchMapLayerProps,
  ParsedClusterPoint,
} from "react-migration/lib/map/layers/ClusteredViewportSearchMapLayer";
import {
  SitesGeoJsonLayer,
  SitesMvtLayer,
} from "react-migration/layouts/map/Multilayer/layer_types/SitesLayerType/SitesGeoJsonLayer";
import { SiteMVTFeature } from "react-migration/layouts/map/Multilayer/layer_types/SitesLayerType/utils";

const TRANSPARENT: Color = [0, 0, 0, 0];
const MULTI_STAGE_CLUSTER_COLOR: Color = [17, 38, 90];
const MULTI_STAGE_CLUSTER_LINE_COLOR: Color = [65, 80, 123];
const SHARED_SITE_COLOR: Color = [1, 235, 199];
const VISIBLE = [1];
const HIDDEN = [-1];

type ClusteredSiteDocument = {
  geometry: Polygon | MultiPolygon | Point;
};

// eslint-disable-next-line @typescript-eslint/ban-types
type ClusteredSiteFeatureProps = {};

// eslint-disable-next-line @typescript-eslint/ban-types
type SitesClusteredAggregations = {
  stage: Record<string, number>;
};

type SitesClusteredViewportSearchMapLayerProps = Omit<
  ClusteredViewportSearchMapLayerProps<
    ClusteredSiteDocument,
    ClusteredSiteFeatureProps,
    SitesClusteredAggregations,
    SitesFilters
  >,
  | "FeatureLayerClass"
  | "getClusterFillColor"
  | "getClusterRadius"
  | "onFetchClusterError"
  | "renderFeatureLayers"
  | "url"
> & {
  stageLookup: Record<string, { color: string }>;
  sitesDirtyCount: number;
};

export type SitesCluster = ParsedClusterPoint<ClusteredSiteDocument, SitesClusteredAggregations>;

export class SitesClusteredViewportSearchMapLayer extends ClusteredViewportSearchMapLayer<
  ClusteredSiteDocument,
  ClusteredSiteFeatureProps,
  SitesClusteredAggregations
> {
  static layerName = "SitesClusteredViewportSearchMapLayer";

  constructor(props: SitesClusteredViewportSearchMapLayerProps) {
    super({
      ...props,
      url: `${ENVIRONMENT.SUPPORTING_SERVICE_URL}/sites-layer/clustered-viewport`,
      dataDeps: [props.sitesDirtyCount],
      clusteringConfig: SITES_CLUSTERING_CONFIG,
      getClusterFillColor: MULTI_STAGE_CLUSTER_COLOR,
      getClusterLineColor: MULTI_STAGE_CLUSTER_LINE_COLOR,
      getClusterTextSize: 12,
      getClusterRadius: 20,
      onClusterHover: props.onClusterHover,
      renderFeatureLayers: (features) => {
        const { stageLookup: stageColourLookup, sitesDirtyCount, filters } = props;
        const { showSharedSites } = filters;
        const visibleFeatureIds = new Set(features.map((feature) => feature.id));
        const { minReturnResultsPrecision } = SITES_CLUSTERING_CONFIG;
        const deckGlZoomOffset = -1;
        const minZoom = minReturnResultsPrecision + deckGlZoomOffset;

        // Use sites service for local development, supporting lambda for all other environments
        const tileUrl =
          ENVIRONMENT.APP_ENV === "local"
            ? `${ENVIRONMENT.SITES_SERVICE_URL}/tiles/{z}/{x}/{y}.mvt`
            : `${ENVIRONMENT.SUPPORTING_SERVICE_URL}/sites-layer/tiles/{z}/{x}/{y}`;

        return [
          new SitesMvtLayer(
            this.getSubLayerProps({
              data: tileUrl,
              binary: true,
              filled: true,
              stroked: true,
              refinementStrategy: "no-overlap",
              minZoom,
              pickable: true,
              getFillColor: TRANSPARENT,
              getLineColor: (feature: SiteMVTFeature) => {
                if (feature.properties.shared) {
                  return SHARED_SITE_COLOR;
                }

                if (!stageColourLookup[feature.properties.stage]) {
                  return TRANSPARENT;
                }

                try {
                  const { r, g, b } = color(stageColourLookup[feature.properties.stage]!.color)!.rgb();
                  return [r, g, b];
                } catch (error) {
                  console.error("Failed to parse stage colour", error);
                  return SHARED_SITE_COLOR;
                }
              },
              updateTriggers: {
                getLineColor: [stageColourLookup, sitesDirtyCount],
                getTileData: [sitesDirtyCount],
                getFilterValue: [...Object.values(props.filters), visibleFeatureIds],
              },
              lineWidthUnits: "pixels",
              pointRadiusUnits: "pixels",
              getLineWidth: 3,
              renderSubLayers: (props: GeoJsonLayerProps) => new SitesGeoJsonLayer(props),

              extensions: [new DataFilterExtension({ filterSize: 1 })],
              getFilterValue: (d: SiteMVTFeature) => {
                if (d.properties.shared) {
                  return showSharedSites ? VISIBLE : HIDDEN;
                }

                return visibleFeatureIds.has(d.properties.id) ? VISIBLE : HIDDEN;
              },
              filterRange: [0, 1],
            })
          ),
        ];
      },
      onFetchClusterError: (error) => {
        console.error(error);

        showError({
          id: `clustered-viewport-layer_${props.id}_search-error`,
          title: "Error searching sites",
          description: "Failed to load sites layer",
          duration: 10_000,
        });
      },
    });
  }
}
